import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  MapPin,
  TrendingUp,
  Clock,
  Copy,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Map as MapIcon,
} from 'lucide-react'
import { trajectoryService, type TrajectoryPoint, type TrajectoryStats } from '../services/trajectoryService'
import { getCurrentConfig } from '../config/api'

// MapModal — Fleet Portal port of the legacy
// balise-management/frontend/src/components/MapModal.tsx. Two modes
// share a single window: Location renders the satellite-tile iframe
// (cheap, key-only), Trajectory renders a Google Maps JS instance with
// a polyline + start/end markers + per-point pins.
//
// We deliberately don't pull @googlemaps/react-wrapper into Fleet
// Portal — ResponsiveFleetMap already lazy-loads the same
// maps.googleapis.com script via its own loader, so we reuse that
// pattern here to keep the bundle slim.

declare global {
  interface Window {
    google: any
  }
}
declare const google: any

export interface MapModalBalise {
  id?: number
  universalId?: string
  lockCode: string
  brand?: string | null
  deviceName?: string | null
  latitude?: number | null
  longitude?: number | null
  lastSeen?: string | null
  status?: string | null
  // When explicitly false, the balise is not mounted on any asset or
  // container, so there is no vehicle to focus on the Fleet Map and the
  // "Open in Fleet Map" action is disabled. Undefined = unknown (enabled).
  isAssigned?: boolean | null
}

export interface MapModalProps {
  balise: MapModalBalise
  mode?: 'location' | 'trajectory'
  onClose: () => void
  // Optional hook so the consumer can also bring its own "Open in
  // Tracking" target (Fleet Map window, asset detail). When omitted,
  // the modal dispatches `fleet:balise-selected` so any listener (e.g.
  // CompleteFleetDashboard) can bring the appropriate window forward.
  onOpenInTracking?: (b: MapModalBalise) => void
}

const TIME_RANGE_OPTIONS: { value: string; labelKey: string; defaultLabel: string }[] = [
  { value: '1h',  labelKey: 'map_modal_range_1h',  defaultLabel: 'Last hour' },
  { value: '6h',  labelKey: 'map_modal_range_6h',  defaultLabel: 'Last 6 hours' },
  { value: '24h', labelKey: 'map_modal_range_24h', defaultLabel: 'Last 24 hours' },
  { value: '3d',  labelKey: 'map_modal_range_3d',  defaultLabel: 'Last 3 days' },
  { value: '7d',  labelKey: 'map_modal_range_7d',  defaultLabel: 'Last week' },
  { value: '30d', labelKey: 'map_modal_range_30d', defaultLabel: 'Last month' },
  { value: '3m',  labelKey: 'map_modal_range_3m',  defaultLabel: 'Last 3 months' },
]

// Pad numbers to two digits — used by formatDateForApi.
const pad = (n: number) => String(n).padStart(2, '0')

// Java's TrajectoryDao expects "yyyy-MM-dd HH:mm:ss". We render the
// date in local time on purpose: the fallback chain ('1h' → '24h' →
// '7d' …) hits whatever GPS data the device has emitted, so a
// reasonable-looking local window is what the operator wants to see.
function formatDateForApi(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function getTimeWindow(period: string): { startDate: string; endDate: string } {
  const now = new Date()
  const endDate = formatDateForApi(now)
  const back = (hrs: number) => formatDateForApi(new Date(now.getTime() - hrs * 60 * 60 * 1000))
  switch (period) {
    case '1h':  return { startDate: back(1),    endDate }
    case '6h':  return { startDate: back(6),    endDate }
    case '24h': return { startDate: back(24),   endDate }
    case '3d':  return { startDate: back(72),   endDate }
    case '7d':  return { startDate: back(168),  endDate }
    case '30d': return { startDate: back(720),  endDate }
    case '3m':  return { startDate: back(2160), endDate }
    default:    return { startDate: back(24),   endDate }
  }
}

function safeDate(value: any, fallback = '—'): string {
  if (!value) return fallback
  let d: Date
  if (Array.isArray(value)) {
    const [y, mo = 1, day = 1, h = 0, mi = 0, s = 0] = value
    d = new Date(y, mo - 1, day, h, mi, s)
  } else {
    d = new Date(value)
  }
  return isNaN(d.getTime()) ? fallback : d.toLocaleString()
}

// Lazy-load the Google Maps JS API. Mirrors ResponsiveFleetMap.tsx so a
// single shared <script> tag serves both windows — no duplicate fetch
// when the operator already had Fleet Map open.
function loadGoogleMapsAPI(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.maps) { resolve(); return }
    const existing = document.getElementById('google-maps-script')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps')))
      return
    }
    const s = document.createElement('script')
    s.id = 'google-maps-script'
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry`
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Failed to load Google Maps API'))
    document.head.appendChild(s)
  })
}

interface TrajectoryMapProps {
  points: TrajectoryPoint[]
  apiKey: string
}

const TrajectoryMap: React.FC<TrajectoryMapProps> = ({ points, apiKey }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const polylineRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const [error, setError] = useState<string | null>(null)
  // The map instance must be state (not a ref): the draw effect below has to
  // re-run once the map is created, because this component mounts with points
  // already loaded — a ref would leave the map permanently blank.
  const [map, setMap] = useState<any>(null)

  useEffect(() => {
    let cancelled = false
    loadGoogleMapsAPI(apiKey)
      .then(() => {
        if (cancelled || !containerRef.current) return
        const m = new google.maps.Map(containerRef.current, {
          zoom: 16,
          center: { lat: 12.5899, lng: -8.0206 },
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          gestureHandling: 'greedy',
          streetViewControl: false,
          fullscreenControl: true,
          mapTypeControl: true,
        })
        // The modal is animated/newly laid out; nudge tile rendering.
        setTimeout(() => google.maps.event.trigger(m, 'resize'), 100)
        setMap(m)
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load map') })
    return () => { cancelled = true }
  }, [apiKey])

  useEffect(() => {
    if (!map || !points || points.length === 0) return

    if (polylineRef.current) {
      polylineRef.current.setMap(null)
      polylineRef.current = null
    }
    markersRef.current.forEach((m) => m.setMap(null))
    markersRef.current = []

    const path = points
      .filter((p) => {
        // Null coords must be dropped BEFORE Number(): Number(null) === 0,
        // which plotted heartbeat rows at (0,0) in the Gulf of Guinea and
        // stretched the polyline across Africa.
        if (p.latitude == null || p.longitude == null) return false
        const lat = Number(p.latitude)
        const lng = Number(p.longitude)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
        if (lat === 0 && lng === 0) return false // GPS cold-start garbage
        return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
      })
      .map((p) => ({ lat: Number(p.latitude), lng: Number(p.longitude) }))

    if (path.length === 0) return

    if (path.length >= 2) {
      polylineRef.current = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#2563eb',
        strokeOpacity: 0.85,
        strokeWeight: 4,
        icons: [{
          icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2, strokeColor: '#2563eb' },
          offset: '100%',
          repeat: '120px',
        }],
      })
      polylineRef.current.setMap(map)
    }

    const bounds = new google.maps.LatLngBounds()
    path.forEach((pt) => bounds.extend(pt))

    const startMarker = new google.maps.Marker({
      position: path[0],
      map,
      title: `${safeDate(points[0]?.timestamp)} — start`,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
          `<svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="14" fill="#16a34a" stroke="#fff" stroke-width="3"/>
            <text x="18" y="23" text-anchor="middle" fill="#fff" font-size="13" font-weight="bold">S</text>
          </svg>`),
        scaledSize: new google.maps.Size(36, 36),
        anchor: new google.maps.Point(18, 18),
      },
      zIndex: 1001,
    })
    const endMarker = new google.maps.Marker({
      position: path[path.length - 1],
      map,
      title: `${safeDate(points[points.length - 1]?.timestamp)} — end`,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
          `<svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
            <circle cx="18" cy="18" r="14" fill="#dc2626" stroke="#fff" stroke-width="3"/>
            <text x="18" y="23" text-anchor="middle" fill="#fff" font-size="13" font-weight="bold">E</text>
          </svg>`),
        scaledSize: new google.maps.Size(36, 36),
        anchor: new google.maps.Point(18, 18),
      },
      zIndex: 1001,
    })
    markersRef.current = [startMarker, endMarker]

    if (path.length === 1) {
      map.setCenter(path[0])
      map.setZoom(17)
    } else {
      map.fitBounds(bounds)
    }

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null)
        polylineRef.current = null
      }
      markersRef.current.forEach((m) => m.setMap(null))
      markersRef.current = []
    }
  }, [map, points])

  return (
    // absolute inset-0: the parent only has min-height (no definite height),
    // so percentage heights (h-full) collapse to 0 and the map renders blank.
    <div className="absolute inset-0">
      <div ref={containerRef} className="absolute inset-0" />
      {!map && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-gray-600 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading map…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 text-amber-700 gap-2">
          <AlertTriangle className="w-5 h-5" />
          <div className="text-sm">{error}</div>
        </div>
      )}
    </div>
  )
}

const MapModal: React.FC<MapModalProps> = ({ balise, mode = 'location', onClose, onOpenInTracking }) => {
  const { t } = useTranslation()
  const apiKey = (getCurrentConfig() as any)?.GOOGLE_MAPS_API_KEY ||
    'AIzaSyAQU_IbHOrrHwF0mKMYaqcwU0vrSTcd9qE'

  const [currentMode, setCurrentMode] = useState<'location' | 'trajectory'>(mode)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [iframeError, setIframeError] = useState(false)

  const [points, setPoints] = useState<TrajectoryPoint[]>([])
  const [stats, setStats] = useState<TrajectoryStats | null>(null)
  const [loadingTraj, setLoadingTraj] = useState(false)
  const [trajectoryError, setTrajectoryError] = useState<string | null>(null)

  const [timeRange, setTimeRange] = useState<string>('24h')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    setCurrentMode(mode)
    setIframeLoaded(false)
    setIframeError(false)
  }, [mode, balise.lockCode])

  const hasCoords = balise.latitude != null && balise.longitude != null

  const loadTrajectory = useCallback(async () => {
    if (!balise?.lockCode || currentMode !== 'trajectory') return
    setLoadingTraj(true)
    setTrajectoryError(null)
    setPoints([])
    setStats(null)
    // Try the requested period first, then fall back to wider windows
    // (matches legacy MapModal behaviour). We stop on the first window
    // that returns at least one point — operators don't want an empty
    // chart when a wider range would yield data.
    const candidates = [timeRange, '24h', '7d', '30d', '3m'].filter(
      (v, i, arr) => arr.indexOf(v) === i,
    )
    let usedRange: string | null = null
    let trajResp: any = null
    let statResp: any = null
    for (const period of candidates) {
      const window = getTimeWindow(period)
      try {
        const [t1, s1] = await Promise.all([
          trajectoryService.getBaliseTrajectory(balise.lockCode, { ...window, limit: 50000 }),
          trajectoryService.getTrajectoryStats(balise.lockCode, window),
        ])
        const arr = Array.isArray(t1?.trajectory) ? t1.trajectory : []
        if (arr.length > 0) {
          trajResp = t1
          statResp = s1
          usedRange = period
          break
        }
      } catch {
        // try next window
      }
    }
    if (trajResp?.trajectory?.length) {
      setPoints(trajResp.trajectory)
      setStats(statResp || null)
      if (usedRange && usedRange !== timeRange) {
        setTrajectoryError(t('map_modal_widened_window', {
          defaultValue: 'No data for {{requested}} — showing {{used}} instead.',
          requested: timeRange,
          used: usedRange,
        }))
      }
    } else {
      const discovery = await trajectoryService.discoverAvailableDataPeriods(balise.lockCode).catch(() => null)
      if (discovery?.hasData) {
        setTrajectoryError(t('map_modal_has_data_elsewhere', {
          defaultValue: 'No data in {{requested}}. Device has {{n}} points overall — try a wider window.',
          requested: timeRange,
          n: discovery.totalPoints || 0,
        }))
      } else {
        setTrajectoryError(t('map_modal_no_data', {
          defaultValue: 'No trajectory data available for this device.',
        }))
      }
    }
    setLoadingTraj(false)
  }, [balise.lockCode, currentMode, timeRange, t])

  useEffect(() => { loadTrajectory() }, [loadTrajectory])

  const coordsLabel = useMemo(() =>
    hasCoords ? `${Number(balise.latitude).toFixed(6)}, ${Number(balise.longitude).toFixed(6)}` : '',
    [balise.latitude, balise.longitude, hasCoords],
  )

  const copyCoords = useCallback(async () => {
    if (!coordsLabel) return
    try { await navigator.clipboard.writeText(coordsLabel) } catch { /* ignore */ }
  }, [coordsLabel])

  const externalUrl = hasCoords
    ? `https://www.google.com/maps?q=${balise.latitude},${balise.longitude}`
    : null

  const handleOpenInTracking = useCallback(() => {
    if (onOpenInTracking) {
      onOpenInTracking(balise)
      return
    }
    // Default behaviour: ask the dashboard to focus the Fleet Map / asset
    // window for this balise. Listeners decide what to do.
    window.dispatchEvent(new CustomEvent('fleet:balise-selected', {
      detail: { lockCode: balise.lockCode, universalId: balise.universalId },
    }))
    onClose()
  }, [balise, onOpenInTracking, onClose])

  const headerTitle = currentMode === 'location'
    ? t('map_modal_title_location', { defaultValue: 'GPS Location' })
    : t('map_modal_title_trajectory', { defaultValue: 'Trajectory' })

  const iframeSrc = hasCoords
    ? `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${balise.latitude},${balise.longitude}&maptype=satellite&zoom=18&center=${balise.latitude},${balise.longitude}`
    : ''

  return (
    <div className="fixed inset-0 z-[310] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-5xl bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
            <MapIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {headerTitle}
              <span className="ml-2 text-gray-500 font-normal">— {balise.universalId || balise.lockCode}</span>
            </div>
            <div className="text-[11px] text-gray-500 truncate">
              {[balise.deviceName, balise.brand, balise.lockCode].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100" title={t('close') as string}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="inline-flex bg-gray-100 rounded-lg p-1 gap-1">
            <button
              type="button"
              onClick={() => setCurrentMode('location')}
              className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 ${
                currentMode === 'location' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <MapPin className="w-3.5 h-3.5" />
              {t('map_modal_tab_location', { defaultValue: 'Location' })}
            </button>
            <button
              type="button"
              onClick={() => setCurrentMode('trajectory')}
              className={`px-3 py-1 text-xs rounded-md flex items-center gap-1 ${
                currentMode === 'trajectory' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <TrendingUp className="w-3.5 h-3.5" />
              {t('map_modal_tab_trajectory', { defaultValue: 'Trajectory' })}
            </button>
          </div>

          {currentMode === 'trajectory' && (
            <div className="inline-flex items-center gap-2 ml-auto">
              <Clock className="w-3.5 h-3.5 text-gray-500" />
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white"
              >
                {TIME_RANGE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey, { defaultValue: o.defaultLabel })}
                  </option>
                ))}
              </select>
              {loadingTraj && <Loader2 className="w-3.5 h-3.5 text-gray-500 animate-spin" />}
            </div>
          )}
        </div>

        {currentMode === 'location' && hasCoords && (
          <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap items-center gap-2 text-xs text-gray-700">
            <span className="font-mono tabular-nums select-all">{coordsLabel}</span>
            <button
              type="button"
              onClick={copyCoords}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-[11px]"
              title={t('map_modal_copy_coords', { defaultValue: 'Copy coordinates' }) as string}
            >
              <Copy className="w-3 h-3" />
              {t('map_modal_copy', { defaultValue: 'Copy' })}
            </button>
            {externalUrl && (
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-[11px]"
                title={t('map_modal_open_external', { defaultValue: 'Open in Google Maps' }) as string}
              >
                <ExternalLink className="w-3 h-3" />
                {t('map_modal_open_external', { defaultValue: 'Open in Google Maps' })}
              </a>
            )}
            {balise.lastSeen && (
              <span className="ml-auto text-[11px] text-gray-500">
                {t('map_modal_fix_at', { defaultValue: 'Fix at' })} {new Date(balise.lastSeen).toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* h-[60vh]: parent flex-col has only maxHeight (auto-sized), so flex-1
            alone gives this area no definite height and the map collapses. */}
        <div className="relative h-[60vh] min-h-[360px]">
          {currentMode === 'location' ? (
            !hasCoords ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-amber-700 gap-2 bg-amber-50/40">
                <AlertTriangle className="w-6 h-6" />
                <div className="text-sm">{t('map_modal_no_coords', { defaultValue: 'No GPS coordinates for this balise yet.' })}</div>
              </div>
            ) : (
              <>
                {!iframeLoaded && !iframeError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-gray-600 gap-2 z-10">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}
                  </div>
                )}
                {iframeError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 text-amber-700 gap-2 z-10">
                    <AlertTriangle className="w-5 h-5" />
                    <div className="text-sm">{t('map_modal_iframe_failed', { defaultValue: 'Map failed to load' })}</div>
                  </div>
                )}
                <iframe
                  src={iframeSrc}
                  loading="eager"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 w-full h-full border-0"
                  title={t('map_modal_iframe_title', { defaultValue: 'Map preview' }) as string}
                  allow="geolocation"
                  onLoad={() => { setIframeLoaded(true); setIframeError(false) }}
                  onError={() => { setIframeError(true); setIframeLoaded(false) }}
                />
              </>
            )
          ) : (
            <>
              {loadingTraj && points.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-gray-600 gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">{t('map_modal_loading_trajectory', { defaultValue: 'Loading trajectory…' })}</span>
                </div>
              ) : points.length === 0 ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-amber-700 gap-2 bg-amber-50/40 px-6 text-center">
                  <AlertTriangle className="w-6 h-6" />
                  <div className="text-sm">
                    {trajectoryError || t('map_modal_no_trajectory', { defaultValue: 'No trajectory data found for this period.' })}
                  </div>
                </div>
              ) : (
                <TrajectoryMap points={points} apiKey={apiKey} />
              )}
            </>
          )}
        </div>

        {currentMode === 'trajectory' && trajectoryError && points.length > 0 && (
          <div className="px-4 py-1.5 border-t border-gray-100 text-[11px] text-amber-700 bg-amber-50/50">
            {trajectoryError}
          </div>
        )}

        {currentMode === 'trajectory' && stats && (
          <div className="px-4 py-2 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <div className="text-gray-500">{t('map_modal_stat_points', { defaultValue: 'Points' })}</div>
              <div className="font-semibold text-gray-900">{stats.total_events ?? points.length}</div>
            </div>
            {stats.avg_speed != null && (
              <div>
                <div className="text-gray-500">{t('map_modal_stat_avg_speed', { defaultValue: 'Avg speed' })}</div>
                <div className="font-semibold text-gray-900">{Number(stats.avg_speed).toFixed(1)} km/h</div>
              </div>
            )}
            {stats.max_speed != null && (
              <div>
                <div className="text-gray-500">{t('map_modal_stat_max_speed', { defaultValue: 'Max speed' })}</div>
                <div className="font-semibold text-gray-900">{Number(stats.max_speed).toFixed(1)} km/h</div>
              </div>
            )}
            {stats.first_event && stats.last_event && (
              <div className="col-span-2 sm:col-span-1">
                <div className="text-gray-500">{t('map_modal_stat_window', { defaultValue: 'Window' })}</div>
                <div className="font-mono text-gray-900 text-[11px]">
                  {safeDate(stats.first_event)} → {safeDate(stats.last_event)}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleOpenInTracking}
            disabled={balise.isAssigned === false}
            title={balise.isAssigned === false
              ? t('map_modal_open_in_tracking_unassigned', { defaultValue: 'This balise is not assigned to an asset — nothing to show on the Fleet Map.' }) as string
              : undefined}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border ${
              balise.isAssigned === false
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700'
            }`}
          >
            <MapPin className="w-3.5 h-3.5" />
            {t('map_modal_open_in_tracking', { defaultValue: 'Open in Fleet Map' })}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default MapModal
